import { useMemo, useState, type ReactNode } from "react"

import { TRACKING_EVENTS, useAnalytics } from "@/lib/analytics"
import {
  TableCsvDownloadDialog,
  TableCsvDownloadTrigger,
} from "@/components/table-actions/TableCsvDownloadButton"

import { RunResultsCsvExportProvider } from "../../contexts/RunResultsCsvExportContext"
import { RunResultsSelectionActionsProvider } from "../../contexts/RunResultsSelectionActionsContext"
import {
  downloadRunResultsCsv,
  type BuildRunResultsCsvParams,
} from "./runResultsCsvExport"

interface RunResultsTableActionsScopeProps extends BuildRunResultsCsvParams {
  isDeletingSelected: boolean
  onDeleteSelected: () => void
  selectedCount: number
  /**
   * Renders the table's own download control, for the surfaces whose controls
   * row has no run actions menu to host the export.
   */
  showToolbarExport: boolean
  /** The run-level actions whose menu hosts what the table publishes here. */
  children: ReactNode
}

/**
 * Publishes the table's own actions — bulk deletion of its selection, and the
 * export of the results it currently shows, in the order it shows them — to
 * the run actions rendered within, and owns the export's confirmation dialog.
 */
export const RunResultsTableActionsScope = ({
  children,
  isDeletingSelected,
  onDeleteSelected,
  selectedCount,
  showToolbarExport,
  ...csvParams
}: RunResultsTableActionsScopeProps) => {
  const { trackEvent } = useAnalytics()
  const [isCsvDialogOpen, setIsCsvDialogOpen] = useState(false)
  const rowCount = csvParams.rows.length

  const exportColumns = useMemo(
    () =>
      csvParams.columnOrder
        .filter((key) => csvParams.visibleColumns.has(key))
        .flatMap((key) => {
          const column = csvParams.allColumns.find((item) => item.key === key)
          return column ? [column.fullLabel || column.label || column.key] : []
        }),
    [csvParams.allColumns, csvParams.columnOrder, csvParams.visibleColumns],
  )
  // The dialog can only confirm an export that has both rows and columns, so
  // the triggers stay closed off rather than opening onto a dead end.
  const isExportDisabled = rowCount === 0 || exportColumns.length === 0

  const selectionActions = useMemo(
    () => ({ isDeletingSelected, onDeleteSelected, selectedCount }),
    [isDeletingSelected, onDeleteSelected, selectedCount],
  )
  const csvExport = useMemo(
    () => ({
      disabled: isExportDisabled,
      onExportCsv: () => setIsCsvDialogOpen(true),
    }),
    [isExportDisabled],
  )

  return (
    <>
      <RunResultsSelectionActionsProvider value={selectionActions}>
        <RunResultsCsvExportProvider value={csvExport}>
          {children}
        </RunResultsCsvExportProvider>
      </RunResultsSelectionActionsProvider>
      {showToolbarExport && (
        <TableCsvDownloadTrigger
          disabled={isExportDisabled}
          onClick={() => setIsCsvDialogOpen(true)}
        />
      )}
      <TableCsvDownloadDialog
        open={isCsvDialogOpen}
        onOpenChange={setIsCsvDialogOpen}
        rowCount={rowCount}
        columns={exportColumns}
        onClick={() => {
          const csv = downloadRunResultsCsv(csvParams)

          trackEvent(TRACKING_EVENTS.RUNS.CSV_EXPORT, {
            table_type: "run_results",
            row_count: rowCount,
            columns: csv.columnCount,
          })
        }}
        disabled={isExportDisabled}
      />
    </>
  )
}
